import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, Download, TrendingUp, Sparkles, Filter } from "lucide-react";
import { RowBin, ClearArea } from "@/components/admin/Purge";
import { toast } from "sonner";

/**
 * /admin/sector-demand — inbound "not seeing your sector?" requests.
 * Shows a top-5 aggregate strip + full request table with inline status.
 */
export default function SectorDemand() {
  const [data, setData] = useState({ items: [], total: 0, top_sectors: [], counts_by_status: {} });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("all");

  const load = async (filter = status) => {
    setLoading(true);
    try {
      const params = filter && filter !== "all" ? { status: filter } : {};
      const res = await api.get("/admin/sector-demand", { params });
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(status); }, [status]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/admin/sector-demand/${id}`, { status: newStatus });
      // Optimistic — refresh totals + list
      setData((d) => ({
        ...d,
        items: d.items.map((r) => (r.id === id ? { ...r, status: newStatus } : r)),
      }));
      toast.success("Updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const exportCsv = async () => {
    // Use fetch → blob → transient <a download> so the Authorization
    // header + httpOnly cookie travel cleanly without a full-page nav
    // and without leaking a token into the URL / browser history.
    try {
      const res = await api.get("/admin/sector-demand/export.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "sector-demand.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sector-demand">
      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10.5px] font-semibold tracking-[0.22em] uppercase text-[#0055FF]">
            Demand signals
          </div>
          <h1 className="mt-1 text-[26px] tracking-[-0.02em] font-semibold text-slate-900">
            Unlisted sector requests
          </h1>
          <p className="mt-1 text-[13.5px] text-slate-500">
            Real-time signals from the /pay-per-call page — every &quot;not seeing your sector?&quot; enquiry lands here.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ClearArea area="sector_demand" onDone={() => load(status)} label="Clear requests" />
          <Button
            variant="outline"
            onClick={exportCsv}
            className="rounded-full border-slate-300"
            data-testid="admin-sd-export"
          >
            <Download className="size-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Counts by status */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-sd-counts">
        <StatusCard label="Total" value={data.total} tone="brand" active={status === "all"} onClick={() => setStatus("all")} />
        <StatusCard label="New" value={data.counts_by_status?.new || 0} tone="blue" active={status === "new"} onClick={() => setStatus("new")} />
        <StatusCard label="Reviewed" value={data.counts_by_status?.reviewed || 0} tone="slate" active={status === "reviewed"} onClick={() => setStatus("reviewed")} />
        <StatusCard label="Launched" value={data.counts_by_status?.launched || 0} tone="green" active={status === "launched"} onClick={() => setStatus("launched")} />
        <StatusCard label="Dismissed" value={data.counts_by_status?.dismissed || 0} tone="muted" active={status === "dismissed"} onClick={() => setStatus("dismissed")} />
      </div>

      {/* Top requested */}
      {data.top_sectors.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5" data-testid="admin-sd-top">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <div className="inline-flex items-center gap-2 text-[10.5px] font-semibold tracking-[0.22em] uppercase text-slate-600">
              <TrendingUp className="size-3 text-[#0055FF]" />
              Top 5 most-requested sectors
            </div>
            <div className="text-[11px] text-slate-400 font-mono">Auto-updated · real-time</div>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {data.top_sectors.map((s, i) => (
              <div key={s.sector_key} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-[11px] font-mono text-slate-400 tabular-nums">#{i + 1}</span>
                  <span className="text-[24px] font-semibold text-slate-900 tabular-nums">{s.count}</span>
                </div>
                <div className="mt-1 text-[13.5px] font-semibold text-slate-900 truncate">{s.sector_name}</div>
                <div className="mt-1 text-[11px] text-slate-500 flex items-center gap-1.5 flex-wrap">
                  {s.roles.map((r) => (
                    <span key={r} className={`inline-flex px-1.5 py-0.5 rounded text-[9.5px] font-medium uppercase tracking-widest ${r === "buyer" ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-slate-100 text-slate-600"}`}>
                      {r}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-900">
            <Filter className="size-3.5 text-slate-500" />
            {status === "all" ? "All requests" : `Status: ${status}`}
            <span className="text-slate-400 font-mono tabular-nums">· {data.items.length}</span>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-16 text-slate-400"><Loader2 className="size-5 animate-spin" /></div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <Sparkles className="size-5 mx-auto text-slate-300" />
            <div className="mt-3 text-[13.5px]">No requests in this filter yet.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="admin-sd-table">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <Th>When</Th>
                  <Th>Sector</Th>
                  <Th>Role</Th>
                  <Th>Volume</Th>
                  <Th>Contact</Th>
                  <Th>Notes</Th>
                  <Th>Status</Th>
                  <Th> </Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50/40" data-testid={`admin-sd-row-${r.id}`}>
                    <Td>{fmt(r.created_at)}</Td>
                    <Td className="font-semibold text-slate-900">{r.sector_name}</Td>
                    <Td>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-medium ${r.role === "buyer" ? "bg-[#0055FF]/10 text-[#0055FF]" : "bg-slate-100 text-slate-600"}`}>
                        {r.role}
                      </span>
                    </Td>
                    <Td className="text-slate-500">{r.volume_hint || "—"}</Td>
                    <Td>
                      <div className="text-slate-900">{r.email}</div>
                      <div className="text-[11.5px] text-slate-500">
                        {[r.name, r.company].filter(Boolean).join(" · ") || "—"}
                      </div>
                    </Td>
                    <Td className="max-w-xs">
                      <div className="text-slate-600 line-clamp-2 leading-snug">{r.notes || "—"}</div>
                    </Td>
                    <Td>
                      <Select value={r.status} onValueChange={(v) => updateStatus(r.id, v)}>
                        <SelectTrigger className="h-8 text-[12px] w-32" data-testid={`admin-sd-status-${r.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="reviewed">Reviewed</SelectItem>
                          <SelectItem value="launched">Launched</SelectItem>
                          <SelectItem value="dismissed">Dismissed</SelectItem>
                        </SelectContent>
                      </Select>
                    </Td>
                    <Td>
                      <RowBin area="sector_demand" id={r.id} name={r.sector_name} onDone={() => load(status)} />
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusCard({ label, value, tone, active, onClick }) {
  const toneCls = {
    brand:  "bg-[#0055FF]/[0.06] border-[#0055FF]/25 text-[#0055FF]",
    blue:   "bg-[#0055FF]/[0.06] border-[#0055FF]/25 text-[#0055FF]",
    slate:  "bg-slate-100 border-slate-200 text-slate-700",
    green:  "bg-emerald-50 border-emerald-200 text-emerald-700",
    muted:  "bg-slate-50 border-slate-200 text-slate-500",
  }[tone] || "bg-white border-slate-200 text-slate-700";
  return (
    <button
      onClick={onClick}
      className={`rounded-xl border p-4 text-left transition-all ${toneCls} ${active ? "ring-2 ring-[#0055FF]/20 shadow-sm" : "hover:shadow-sm"}`}
      data-testid={`admin-sd-count-${label.toLowerCase()}`}
    >
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] opacity-70">{label}</div>
      <div className="mt-1 text-[24px] font-semibold tabular-nums">{value}</div>
    </button>
  );
}
function Th({ children }) { return <th className="text-left px-4 py-2.5 text-[10.5px] font-semibold uppercase tracking-[0.14em]">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>; }
function fmt(iso) {
  try {
    const d = new Date(iso);
    return `${d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })} · ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  } catch { return iso; }
}
